import { useState } from 'react';
import type { FieldMetadata } from '../../data/client';
import { useComments } from '../../data/hooks';
import { DropdownMenu, DropdownRadioGroup, DropdownRadioItem, Icon } from '../../ui';
import { CommentEntryRow, type CommentTarget, CommentThread } from './CommentThread';
import { NoteComposer } from './NoteComposer';
import { NotesPanel } from './NotesPanel';
import { RecordTimeline } from './RecordTimeline';

/**
 * THE ACTIVITY FEED, filtered (notes-tasks-comments.md 12.1).
 *
 * NOTES AND COMMENTS ARE HISTORY, so they live here rather than beside the fields -
 * the mirror image of the tasks decision, which put state next to the values it is
 * about. Eleven new event actions make a feed longer, and the READING answer is this
 * filter; the SERVING answer is the keyset pagination the module added to the timeline.
 *
 * WAVE 4 (C2-12): THE FILTER IS A POP-UP BUTTON, NOT A SECOND SEGMENTED CONTROL. What the
 * owner photographed was two segmented controls stacked - Overview | Activity, and under it
 * All | Notes | Comments | Changes in the accent-filled variant - which reads as two
 * competing scopes and spends the screen's one selected-segment accent twice ("Sieht weird
 * aus hier", owner-88). The record has ONE content segmented control and it is the pane's
 * (`spec/screens/record.md` section 2 item 5a); a list's filter is a button that opens a
 * menu at the trailing end of the list's header, which is what Mail does above its message
 * list: "Click the Filter button at the top of the message list ... then choose Enable
 * Message Filter from the menu that appears".
 * support: https://support.apple.com/guide/mail/search-for-emails-mlhlp1003/mac
 *
 * P5: THE STREAM IS THE MAIN COLUMN, so the cards that used to wrap each filter's
 * content are gone - a stream inside a box inside a pane is the nested frame the HIG's
 * layout rule forbids. What is left is the header line, an entry row that opens the writing
 * panel, and the rows.
 *
 * THE FILTER STILL READS All, Notes, Comments and Changes. `spec/screens/record.md`
 * section 2 item 5 names All, Notes, Tasks, Calls and Changes; Tasks and Calls are not
 * event kinds this product's log separates, so drawing them would be an item that filters
 * nothing (P5-4: the timeline shows only real facts). The two missing ones are listed
 * OPEN on `/dev/record-states`.
 */
/**
 * kit: `Apple macOS 27 UI Kit.sketch / Pop-up Buttons/Light/Content Area/3 Rg/1 - Idle`, height 24px and radius 6.
 * kit: `Apple macOS 27 UI Kit.sketch / Pop-up Buttons/Dark/Content Area/3 Rg/1 - Idle`, height 24px and radius 6.
 */
const FILTERS = ['all', 'notes', 'comments', 'changes'] as const;
export type ActivityFilter = (typeof FILTERS)[number];

const FILTER_LABELS: Record<ActivityFilter, string> = {
  all: 'All',
  notes: 'Notes',
  comments: 'Comments',
  changes: 'Changes',
};

/**
 * THE ENTRY ROW'S VERB FOLLOWS THE FILTER, because the row writes into what the reader is
 * looking at: under Comments a "New Note…" row would put the thing it wrote one filter
 * away. Changes are what the system recorded and nobody writes one, so that filter has no
 * entry row at all.
 */
const ENTRY_VERB: Record<ActivityFilter, string | null> = {
  all: 'New note…',
  notes: 'New note…',
  comments: 'New comment…',
  changes: null,
};

export interface ActivityFeedProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly fields: readonly FieldMetadata[];
  readonly filter?: ActivityFilter | undefined;
  readonly onFilterChange?: ((filter: ActivityFilter) => void) | undefined;
  /**
   * The entry row at the top of the stream (section 2 item 5) when it writes a NOTE, which
   * is the record page's own panel so a draft survives the toolbar's Add note. `undefined`
   * on an archived record, where there is nothing to add. Comments open this component's
   * own panel - the same construction, one filter along - so a caller that only knows about
   * notes needs no new prop.
   */
  readonly onAddNote?: (() => void) | undefined;
}

export function ActivityFeed({
  objectId,
  recordId,
  fields,
  filter: controlledFilter,
  onFilterChange,
  onAddNote,
}: ActivityFeedProps) {
  const [localFilter, setLocalFilter] = useState<ActivityFilter>('all');
  const filter = controlledFilter ?? localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;
  /** The record's one writing panel, aimed at whatever asked for it (`CommentTarget`). */
  const [target, setTarget] = useState<CommentTarget | null>(null);
  const archived = onAddNote === undefined;
  const verb = archived ? null : ENTRY_VERB[filter];

  return (
    /*
      A NAMED REGION whose heading is not drawn, for the reason section 7 gives: Apple
      does not repeat the toolbar title as a heading inside the content, and the stream
      IS the content of this column. The name is what a screen reader and a test have to
      hold on to.
    */
    <section aria-label="Activity" className="min-w-0">
      <h2 className="sr-only">Activity</h2>
      {/* ONE LINE, and the filter at its trailing end: Mail's message-list header. */}
      <div className="record-stream-header">
        <DropdownMenu
          align="end"
          trigger={
            <button type="button" className="record-popup">
              {/* THE NAME SAYS WHAT THE CONTROL DOES AND THE FACE SAYS WHAT IT IS SET TO,
                  which is what a pop-up button is: "Show All" read aloud, "All" drawn. An
                  `aria-label` would have replaced the value rather than introduced it. */}
              <span className="sr-only">Show </span>
              <span>{FILTER_LABELS[filter]}</span>
              {/* The kit's pop-up button carries one glyph in its trailing 24 slot; ours is
                  the chevron the product already draws for a control that opens downwards
                  (`ui/Icon`, no new symbol). WHICH SF Symbol the kit's own glyph is, is
                  OPEN: the master stores it as a private-use codepoint, and searching
                  developer.apple.com/sf-symbols and the kit's layer names for a name gave
                  none. */}
              <Icon name="chevron-down" size="0.75rem" className="shrink-0 text-ink-muted" />
            </button>
          }
        >
          {/*
            NO HEADING INSIDE THE MENU (wave 4 round 2). Round 1 passed the group a label, so
            the menu opened with "CHOOSE" in tracked capitals over its four items. Apple's
            pop-up menu has no heading: the tick marks the current item and the button's own
            face says what the choice is set to, which is the whole of what a heading would
            have said. R10's "Choose" is the verb a pop-up's own PLACEHOLDER takes, never a
            title inside its menu. The primitive is untouched - it draws a label only when one
            is passed, and none is.
            kit: Apple macOS 27 UI Kit.sketch / Menus/Light/Examples/Regular
          */}
          <DropdownRadioGroup
            value={filter}
            onValueChange={(value) => setFilter(value as ActivityFilter)}
          >
            {FILTERS.map((entry) => (
              <DropdownRadioItem key={entry} value={entry}>
                {FILTER_LABELS[entry]}
              </DropdownRadioItem>
            ))}
          </DropdownRadioGroup>
        </DropdownMenu>
      </div>

      {/*
        THE STREAM'S ENTRY ROW (wave 3, R-B1 and R-B6), which is what the ghost "Add a note"
        capsule beside the chip row became. Two controls one word apart - "Add note" in the
        toolbar and "Add a note" here - were two ways to start the same note, and the
        toolbar's is the screen's one prominent action. `spec/screens/record.md` section 2
        item 5 puts a composer ENTRY ROW at the top of the stream instead, so the stream
        begins with a row in the stream's own construction and opens the same panel.
      */}
      {verb !== null &&
        (filter === 'comments' ? (
          <CommentEntryRow label={verb} onClick={() => setTarget({})} />
        ) : (
          <button type="button" onClick={onAddNote} className="record-entry-row">
            <span className="record-timeline-glyph">
              <Icon name="pencil" size="0.9rem" />
            </span>
            <span>{verb}</span>
          </button>
        ))}

      {filter === 'notes' && (
        <NotesPanel
          objectId={objectId}
          recordId={recordId}
          {...(archived ? {} : { onCompose: setTarget })}
        />
      )}

      {filter === 'comments' && (
        <RecordDiscussion
          objectId={objectId}
          recordId={recordId}
          {...(archived ? {} : { onCompose: setTarget })}
        />
      )}

      {(filter === 'all' || filter === 'changes') && (
        <RecordTimeline
          objectId={objectId}
          recordId={recordId}
          fields={fields}
          hideAnnotations={filter === 'changes'}
        />
      )}

      {/* THE SAME PANEL THE RECORD'S NOTES USE, one kind along (section 2 item 7). */}
      <NoteComposer
        objectId={objectId}
        recordId={recordId}
        kind="comment"
        open={target !== null}
        focusRequest={target === null ? 0 : 1}
        {...(target?.noteId === undefined ? {} : { noteId: target.noteId })}
        {...(target?.replyToCommentId === undefined
          ? {}
          : { replyToCommentId: target.replyToCommentId })}
        {...(target?.replyToName === undefined ? {} : { replyToName: target.replyToName })}
        onOpenChange={(next) => {
          if (!next) setTarget(null);
        }}
      />
    </section>
  );
}

/**
 * The record's own discussion: comments NOT hosted on a note. The composer that used to be
 * pinned at its bottom is the panel above; what is left here is the list and the one line
 * that says what a comment is for.
 */
function RecordDiscussion({
  objectId,
  recordId,
  onCompose,
}: {
  readonly objectId: string;
  readonly recordId: string;
  readonly onCompose?: ((target: CommentTarget) => void) | undefined;
}) {
  const comments = useComments(objectId, recordId);
  const onRecord = (comments.data ?? []).filter((comment) => comment.noteId === null);

  return (
    <>
      {/* ONE LINE INSIDE THE STREAM, not a block (section 4, and the same argument
          `RecordTimeline` makes for its own empty state): the entry row is directly above, so
          the sentence says what a comment is FOR rather than repeating the action. */}
      {onRecord.length === 0 && (
        <p className="record-meta-line py-4">
          No comments. Comments are for talking something over; a note is for writing down what
          happened.
        </p>
      )}
      <CommentThread
        comments={onRecord}
        objectId={objectId}
        recordId={recordId}
        onCompose={onCompose ?? (() => undefined)}
      />
    </>
  );
}
